import React from "react";
import { CheckCircle2, X, Edit3, RefreshCw } from "lucide-react";
import { useGet, apiPost } from "../../hooks/useApi.js";
import { toast } from "sonner";
import { formatHours } from "../../lib/format.js";

/**
 * Issue #51 — time-entry autopilot review section.
 *
 * Shows reviewable time-entry drafts derived from ticket activity (notes,
 * linked calls, schedule sessions). The reviewer confirms (posts a real CW
 * time entry — board policy applies), edits then confirms, or rejects with a
 * reason. Nothing is ever auto-posted; drafts are never fabricated and each
 * shows its confidence, reason and per-source evidence.
 */

interface TimeDraftSource {
  kind: "time_entries" | "notes" | "calls" | "schedule";
  label: string;
  hours: number | null;
  note: string;
}

interface TimeDraft {
  id: string;
  ticketId: string;
  cwTicketId: number;
  hours: number;
  timeStart: string;
  timeEnd: string;
  notes: string;
  internalNotes: string | null;
  billableOption: string;
  memberIdentifier: string | null;
  confidence: number;
  reason: string;
  sources: TimeDraftSource[];
  status: string;
  rejectedReason: string | null;
  postedAt: string | null;
  cwTimeEntryId: number | null;
  createdAt: string;
}

export function TimeDraftsReview({
  ticketId,
  onRefresh,
}: {
  ticketId: string;
  onRefresh: () => Promise<unknown>;
}) {
  const { data, loading, refetch } = useGet<{ enabled: boolean; items: TimeDraft[] }>(
    ticketId ? `/api/v1/tickets/${ticketId}/time-drafts` : null,
  );
  const [editing, setEditing] = React.useState<string | null>(null);
  const [editHours, setEditHours] = React.useState("");
  const [editNotes, setEditNotes] = React.useState("");
  const [busy, setBusy] = React.useState<string | null>(null);

  async function generate() {
    setBusy("generate");
    try {
      await apiPost(`/api/v1/tickets/${ticketId}/time-drafts/generate`, {});
      toast.success("Draft refreshed from ticket activity.");
      await Promise.all([refetch(), onRefresh()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not draft a time entry");
    } finally {
      setBusy(null);
    }
  }

  async function confirm(draft: TimeDraft, edits?: { hours: number; notes: string }) {
    setBusy(draft.id);
    try {
      const view = await apiPost<{ status: string; cwTimeEntryId: number | null }>(
        `/api/v1/tickets/${ticketId}/time-drafts/${draft.id}/confirm`,
        edits ?? {},
      );
      toast.success(
        view.cwTimeEntryId != null
          ? `Time entry posted to ConnectWise (#${view.cwTimeEntryId}).`
          : "Draft confirmed.",
      );
      setEditing(null);
      await Promise.all([refetch(), onRefresh()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not confirm the draft");
    } finally {
      setBusy(null);
    }
  }

  async function reject(draft: TimeDraft) {
    setBusy(draft.id);
    try {
      const reason = window.prompt("Why reject this draft? (optional, saved for feedback)") ?? undefined;
      await apiPost(`/api/v1/tickets/${ticketId}/time-drafts/${draft.id}/reject`, reason ? { reason } : {});
      toast.success("Draft rejected.");
      await Promise.all([refetch(), onRefresh()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not reject the draft");
    } finally {
      setBusy(null);
    }
  }

  const items = data?.items ?? [];
  const openDrafts = items.filter((d: TimeDraft) => d.status === "draft" || d.status === "pending_review");
  const closedDrafts = items.filter((d: TimeDraft) => d.status === "posted" || d.status === "rejected").slice(0, 3);

  return (
    <section aria-label="Time-entry autopilot" className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-[hsl(var(--content))]">Time-entry autopilot</h2>
        <button
          type="button"
          onClick={generate}
          disabled={busy === "generate"}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-2.5 py-1.5 text-xs font-medium text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-hover))] disabled:opacity-50"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Draft from activity
        </button>
      </div>
      <p className="mt-1 text-xs text-[hsl(var(--content-subtle))]">
        Drafts time entries from ticket activity for review — nothing is posted to ConnectWise until you confirm.
      </p>

      {loading ? (
        <div className="mt-3 space-y-2" aria-busy="true">
          <div className="h-16 animate-pulse rounded-lg bg-[hsl(var(--surface-hover))]" />
        </div>
      ) : openDrafts.length === 0 ? (
        <p className="mt-3 text-sm text-[hsl(var(--content-subtle))]">
          {data?.enabled
            ? "No open draft. Use “Draft from activity” to compute one from notes, calls and schedule sessions."
            : "Autopilot is off for this tenant. An admin can enable it in Admin > Settings."}
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {openDrafts.map((d: TimeDraft) => (
            <li
              key={d.id}
              data-testid={`time-draft-${d.id}`}
              className="rounded-lg border border-[hsl(var(--border))] p-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="text-sm font-semibold text-[hsl(var(--content))]">
                    {formatHours(d.hours)} proposed
                  </span>
                  {d.memberIdentifier && (
                    <span className="ml-2 text-xs text-[hsl(var(--content-subtle))]">for {d.memberIdentifier}</span>
                  )}
                </div>
                <span
                  className="rounded-full bg-[hsl(var(--surface-hover))] px-2 py-0.5 text-xs text-[hsl(var(--content-subtle))]"
                  title={d.reason}
                >
                  confidence {Math.round(d.confidence * 100)}%
                </span>
              </div>
              <p className="mt-1 text-xs text-[hsl(var(--content-subtle))]">{d.reason}</p>
              {d.sources.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {d.sources.map((s: TimeDraftSource, i: number) => (
                    <li key={i} className="text-xs text-[hsl(var(--content-subtle))]">
                      · {s.label}
                      {s.note.startsWith("Unavailable") ? " — " + s.note : ""}
                    </li>
                  ))}
                </ul>
              )}

              {editing === d.id ? (
                <div className="mt-3 space-y-2">
                  <label className="block text-xs font-medium text-[hsl(var(--content-subtle))]" htmlFor={`draft-hours-${d.id}`}>
                    Hours
                  </label>
                  <input
                    id={`draft-hours-${d.id}`}
                    type="number"
                    min="0.25"
                    max="24"
                    step="0.25"
                    list="quarter-hour-steps"
                    value={editHours}
                    onChange={(e) => setEditHours(e.target.value)}
                    aria-describedby={`draft-hours-hint-${d.id}`}
                    className="w-32 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))]"
                  />
                  <datalist id="quarter-hour-steps">
                    {Array.from({ length: 12 }, (_, i) => (i + 1) * 0.25).map((h) => (
                      <option key={h} value={h} />
                    ))}
                  </datalist>
                  <p id={`draft-hours-hint-${d.id}`} className="text-xs text-[hsl(var(--content-subtle))]">
                    Quarter-hour steps (15-minute increments)
                  </p>
                  <label className="block text-xs font-medium text-[hsl(var(--content-subtle))]" htmlFor={`draft-notes-${d.id}`}>
                    Notes
                  </label>
                  <textarea
                    id={`draft-notes-${d.id}`}
                    rows={2}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy === d.id}
                      onClick={() => {
                        const hours = Number(editHours);
                        if (!Number.isFinite(hours) || hours <= 0 || hours > 24) {
                          toast.error("Enter hours between 0 and 24.");
                          return;
                        }
                        // Snap to the quarter hour on confirm so the posted
                        // entry round-trips a 0.25h step cleanly.
                        confirm(d, { hours: Math.round(hours * 4) / 4, notes: editNotes });
                      }}
                      className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                      Confirm {formatHours(Number(editHours) || d.hours)}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(null)}
                      className="rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--content))]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={busy === d.id}
                    onClick={() => confirm(d)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Confirm &amp; post {formatHours(d.hours)}
                  </button>
                  <button
                    type="button"
                    disabled={busy === d.id}
                    onClick={() => {
                      setEditing(d.id);
                      setEditHours(String(Math.round(d.hours * 4) / 4));
                      setEditNotes(d.notes);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-hover))] disabled:opacity-50"
                  >
                    <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
                    Edit
                  </button>
                  <button
                    type="button"
                    disabled={busy === d.id}
                    onClick={() => reject(d)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-hover))] disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                    Reject
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {closedDrafts.length > 0 && (
        <div className="mt-3 border-t border-[hsl(var(--border))] pt-2">
          <h3 className="text-xs font-semibold uppercase text-[hsl(var(--content-subtle))]">Recent outcomes</h3>
          <ul className="mt-1 space-y-0.5">
            {closedDrafts.map((d: TimeDraft) => (
              <li key={d.id} className="text-xs text-[hsl(var(--content-subtle))]">
                {d.status === "posted"
                  ? `Posted ${formatHours(d.hours)}${d.cwTimeEntryId != null ? ` (CW #${d.cwTimeEntryId})` : ""}`
                  : `Rejected ${formatHours(d.hours)}${d.rejectedReason ? ` — ${d.rejectedReason}` : ""}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
